import { useEffect, useState } from "react";
import {
  Spin,
  Steps,
  Typography,
} from "antd";
import { Icon } from 'Components';
import Step1 from "./CampaignSteps/Step1";
import Step2TestConfiguration from "./CampaignSteps/Step2TestConfiguration";
import Step4DeliverySchedule from "./CampaignSteps/Step4DeliverySchedule";
import Step3Recipients from "./CampaignSteps/Step3Recipients";
import Step5ReviewStart from "./CampaignSteps/Step5ReviewStart";
import { CampaignThresholdData } from "./PhishingManager";
import ExitCampaignModal from "./CampaignSteps/Modals/ExitCampaignModal";
import { getPhishingCampaignFilters, getPhishingCampaignTemplatePreviewDetails } from "Services/Api/PhishingManager/PhishingManager.Service";
import PhishingModal from "./CampaignSteps/Modals/PhishingModal";

const { Title, Text } = Typography;
const { Step } = Steps;

type CampaignSetupProps = {
  tenantId: number | string | undefined;
  onBack: () => void;
  thresholdData: null | CampaignThresholdData
};

export interface FilterResults {
  languages: { id: number, name: string }[],
  difficulties: string[],
  categories: { id: number, name: string }[],
}

export interface TemplateLanguage {
  id: number;
  name?: string;
}

export interface TemplateDetails {
  id: number;
  name: string;
  description: string | null;
  difficulty: "beginner" | "intermediate" | "advanced" | string;
  email_template_html: string;
  landing_page_html: string;
  image_path: string;
  selected_packaged_template_language_id: number;
  languages: TemplateLanguage[];
}

export default function CampaignSetup({ onBack, tenantId, thresholdData }: CampaignSetupProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showPhisingModal, setShowPhisingModal] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [filterResults, setFilterResults] = useState<FilterResults | null>(null);
  const [templateDetails, setTemplateDetails] = useState<TemplateDetails | null>(null);

  const DESKTOP_STEPS = [
    "Scenario",
    <>Test<br />Configuration</>,
    <>Full Campaign<br />Recipients</>,
    <>Delivery &<br />Schedule</>,
    <>Review &<br />Start Test</>,
  ];

  const MOBILE_STEPS = [
    "Scenario",
    "Test",
    "Recipients",
    "Delivery",
    "Review",
  ];

  const STEPS = isMobile ? MOBILE_STEPS : DESKTOP_STEPS;

  const handleClose = () => onBack();
  const handleNext = () => setCurrentStep((s) => Math.min(s + 1, 4));
  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));
  const handleExitCampaign = () => setShowExitModal(true);

  const fetchFilterCampaigns = async (value?: string) => {
    setIsLoading(true);
    try {
      const response = await getPhishingCampaignFilters(tenantId, value ?? "");
      setFilterResults(response.data)
    } catch (error: any) {
      setFilterResults(null)
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewTemplate = async (id: string | number | null) => {
    if (!!id) {
      setSelectedTemplate(id);
      try {
        setShowPhisingModal(true);
        setIsLoading(true);
        const response =
          await getPhishingCampaignTemplatePreviewDetails(
            tenantId,
            id ?? "",
          );
        setTemplateDetails(response.data);
      } catch (err) {
        setShowPhisingModal(false);
        setTemplateDetails(null);
        setSelectedTemplate(null);
      } finally {
        setIsLoading(false);
      }
    } else {
      setShowPhisingModal(false);
      setTemplateDetails(null);
      setSelectedTemplate(null);
    }
  }

  const loadPreviewDetails = async (langId: string | number | null) => {
    setIsLoading(true);
    try {
      const response =
        await getPhishingCampaignTemplatePreviewDetails(
          tenantId,
          templateDetails?.id ?? "",
          langId ?? ""
        );
      setTemplateDetails(response.data);
    } catch (err) {
      setTemplateDetails(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchFilterCampaigns();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Spin spinning={isLoading}>
      <div className="phishingManager">
        <div className="phishingManagerStep1Wrapper">

          <div className="d-flex align-start justify-between mb-40">
            <div className="mw-880">
              <PhishingModal
                loading={isLoading}
                visible={showPhisingModal}
                templateDetails={templateDetails}
                onClose={() => setShowPhisingModal(false)}
                onNext={() => {
                  handleNext();
                  setShowPhisingModal(false);
                }}
                onLanguageChange={(langId: number) => {
                  loadPreviewDetails(langId)
                }}
              />
              <ExitCampaignModal visible={showExitModal} onClose={() => setShowExitModal(false)} onConfirm={handleClose} />
              <Title level={3} className="campaign-title">
                Campaign setup
              </Title>
              <Text className="campaign-subtitle">
                Follow the steps to create a new campaign
              </Text>
            </div>
            <button
              onClick={handleExitCampaign}
              className="campaign-setup-close-wrapper"
              title="Close"
            >
              <Icon type="campaignSetupClose" />
            </button>
          </div>
          <div className="w-full d-flex flex-column align-center justify-center">
            <div className="phishing-step1">
              <div className="mw-900 mx-auto">
                <Steps
                  current={currentStep}
                  className="mw-900 mx-auto d-flex gap-70"
                  size="small" labelPlacement="vertical"
                >
                  {STEPS.map((label, i) => (
                    <Step
                      status={
                        i < currentStep
                          ? "finish"
                          : i === currentStep
                            ? "process"
                            : "wait"
                      }
                      className="d-flex justify-center"
                      style={{ cursor: i <= currentStep ? "pointer" : "default" }}
                      title={label} />))}
                </Steps>
              </div>
            </div>
            {currentStep === 0 && (
              <Step1
                tenantId={tenantId}
                selectedTemplate={selectedTemplate}
                thresholdData={thresholdData}
                filterResults={filterResults}
                onSelect={handlePreviewTemplate}
                onNext={handleNext}
                onClose={handleClose}
              />
            )}

            {currentStep === 1 && (
              <Step2TestConfiguration
                onBack={handleBack}
                onNext={handleNext}
              />
            )}

            {currentStep === 2 && (
              <Step3Recipients
                onBack={handleBack}
                onNext={handleNext}
              />
            )}

            {currentStep === 3 && (
              <Step4DeliverySchedule
                onBack={handleBack}
                onNext={handleNext}
              />
            )}

            {currentStep === 4 && (
              <Step5ReviewStart
                onBack={handleBack}
                onFinish={() => {
                  alert("Campaign launched successfully!");
                  setCurrentStep(0);
                  setSelectedTemplate(null);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Spin>
  );
}
